import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    RadioButton
} from 'common/components';
import { MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE, MOVE_VALIDATION_ERROR_MESSAGE } from 'common/config';
import { ButtonLabel, ButtonVariant, CategoryField, EntityType, MessageType } from 'common/enums';
import { masterDataActions } from 'modules/master-data/redux';
import { addValidationError, removeValidationError } from 'common/utils/error';
import {
    ACTION_ITEMS,
    CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    CATEGORY_DESCRIPTION_MAX_LENGTH,
    CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    CATEGORY_NAME_EMPTY_ERROR,
    CATEGORY_NAME_INVALID_CHAR_ERROR,
    CATEGORY_NAME_MAX_LENGTH,
    CATEGORY_NAME_MAX_LENGTH_ERROR,
    CATEGORY_ORDER_EMPTY_ERROR,
    CATEGORY_ORDER_GREATER_THAN_ZERO,
    CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO,
    SUB_CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_ORDER_MAX_LENGTH,
    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR,
    CATEGORY_ORDER_MAX_LENGTH,
    CATEGORY_ORDER_MAX_LENGTH_ERROR
} from 'common/config';
import { FieldError } from 'common/models';
import CategoryForm from 'common/components/custom/category-form/CategoryForm';
import { SubCategoryField } from 'common/enums/data.enum';
import SubcategoryForm from 'common/components/custom/sub-category-form/SubcategoryForm';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import SingleSelectMasterData from '../single-select-master-data/SingleSelectMasterData';
import { validateInputString, validateMaxLength, validateNumbers } from 'common/utils/validator';

import './MoveWizardPopup.scss';
import {
    MOVED_SUBCATEGORY_WARNING_MESSAGE,
    MOVED_SUBCATEGORY_WARNING_TITLE
} from 'common/config/message.config';

type MoveWizardPopupProps = {
    isVisible: boolean;
    closePopupHandler: () => void;
    updateSelectedRadioButton: (entity: EntityVM) => void;
    entityId: string;
    entityType: EntityType;
    modalDetails: any;
    saveHandler: (createNew: string, data: any) => void;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    fieldErrors: Array<FieldError>;
    hierarchicalData: MasterDataEntity[];
    toggleExpansion: (payload: EntityVM) => void;
    openPopUpToCreateNewSubCategory: () => void;
    setCreateNewEntityType: (entityType: string) => void;
    fetchNextHierachySet: () => void;
    primaryButtonDisable: boolean;
};

export default function MoveWizardPopup({
    isVisible,
    closePopupHandler,
    updateSelectedRadioButton,
    entityId,
    modalDetails,
    saveHandler,
    updateFieldErrors,
    fieldErrors,
    hierarchicalData,
    toggleExpansion,
    openPopUpToCreateNewSubCategory,
    setCreateNewEntityType,
    fetchNextHierachySet,
    primaryButtonDisable
}: MoveWizardPopupProps) {
    const [popupDetails, setPopupDetails] = useState({
        title: MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE,
        text: MOVE_VALIDATION_ERROR_MESSAGE,
        type: ''
    });

    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [entityIdVal, setEntityIdVal] = useState(entityId);
    const [createNewSubCategoryFlag, setCreateNewSubCategoryFlag] = useState('');
    const [targetModelData, setTargetModelData] = useState<any>();

    const [name, setName] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [description, setDescription] = useState('');

    const [subcatName, setSubcatName] = useState('');
    const [subcatSortOrder, setSubcatSortOrder] = useState('');
    const [subcatDescription, setSubcatDescription] = useState('');

    const dispatch = useDispatch();

    const handleRadioClicked = (data: EntityVM) => {
        /**
         * Following if condition will check if source and target is same
         */
        if (entityIdVal == data.id) {
            setPopupDetails({
                title: MOVED_SUBCATEGORY_WARNING_TITLE,
                text: MOVED_SUBCATEGORY_WARNING_MESSAGE,
                type: 'SourceTargetSame'
            });
            setShowPopup(true);
        } else {
            setCreateNewSubCategoryFlag('');

            setCreateNewEntityType('');

            setTargetModelData(data);

            updateSelectedRadioButton(data);
        }
    };

    useEffect(() => {
        setEntityIdVal(entityIdVal);
    }, [entityIdVal]);

    /**
     * Following state variable will be used to deside which radio button should be selected.
     * Existing subcategory radio button group or create new categry or subcategory radio button group.
     */

    const onClickOfRadioButton = (radioButtonValue: string) => {
        setCreateNewSubCategoryFlag(radioButtonValue);

        setCreateNewEntityType(radioButtonValue);

        dispatch(masterDataActions.deselectRadioButton());
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        if (validateMaxLength(event.target.value, CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_EMPTY_ERROR);
        }
        if (validateMaxLength(event.target.value, CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_MAX_LENGTH_ERROR);
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const handleSubcatDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Description);
        if (validateMaxLength(event.target.value, SUB_CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setSubcatDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSubcatNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_EMPTY_ERROR
            );
        }
        if (validateMaxLength(event.target.value, SUB_CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setSubcatName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSubcatSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.SortOrder);
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    SubCategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        SubCategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > SUB_CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        SubCategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                SubCategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSubcatSortOrder(value);
    };

    const onBackButtonClicked = () => {
        if (wizardScreen == 2) {
            updateFieldErrors([]);
            setWizardScreen(1);
        } else if (wizardScreen == 1) {
            updateFieldErrors([]);
            setWizardScreen(0);
        }
    };

    const [wizardScreen, setWizardScreen] = useState(0);

    const cancelMoveWizardPopup = () => {
        setName('');
        setSortOrder('');
        setDescription('');
        setSubcatName('');
        setSubcatSortOrder('');
        setSubcatDescription('');
        setWizardScreen(0);
        setCreateNewSubCategoryFlag('');
        setTargetModelData(null);
        closePopupHandler();
    };

    return (
        <>
            <Modal
                dat-id={'search'}
                show={isVisible}
                onClose={() => {
                    setTargetModelData(null);
                    closePopupHandler();
                    updateFieldErrors([]);
                }}
                style={{ width: '50vw', height: '90vh' }}
                className="hierarchical-view-popup-main-container">
                <ModalHeader>{`${modalDetails.title}${
                    wizardScreen === 1
                        ? ': Create Category'
                        : wizardScreen === 2
                        ? ': Create Sub-Category'
                        : ''
                }`}</ModalHeader>
                <ModalBody>
                    {wizardScreen == 0 ? (
                        <div style={{ height: '45vh' }}>
                            {' '}
                            {/** 35vh with radio button */}
                            <div style={{ height: '100%' }}>
                                <Label text={modalDetails.text}></Label>
                                <SingleSelectMasterData
                                    hideSelectionOnCategory={
                                        modalDetails.type === 'moveIndicator' ? true : false
                                    }
                                    hierarchicalData={hierarchicalData}
                                    updateSelectedRadioButton={handleRadioClicked}
                                    toggleExpansion={toggleExpansion}
                                    hideChildIndicatorsOnly={true}
                                    preventSelectionOfSubCategoriesHavingSubCategories={
                                        modalDetails.type === 'moveIndicator' ? true : false
                                    }
                                    preventSelectionOfSubCategoriesHavingIndicators={
                                        modalDetails.type === 'moveIndicator' ? false : true
                                    }
                                    fetchNextHierachySet={fetchNextHierachySet}
                                />
                            </div>
                            {
                                /**
                                 * If Create new category and subcategory feature require
                                 * Make display "flex" instead of "none".
                                 * To hide this feature, display is none
                                 */
                                <div style={{ display: 'flex', height: '10%', marginTop: '0rem' }}>
                                    <div className="create-new-category-checkbox-label">
                                        <RadioButton
                                            id={'createNewSubCategory1'}
                                            checked={
                                                createNewSubCategoryFlag === 'createNewCategory'
                                            }
                                            name={'createNewSubCategory'}
                                            value={'createNewCategory'}
                                            onChange={(event: any) => {
                                                onClickOfRadioButton(event.target.value);
                                            }}
                                        />
                                        <Label
                                            style={{ paddingTop: '0rem' }}
                                            text="Category/Sub category not found. Create new"></Label>
                                    </div>
                                    <div className="create-new-category-checkbox-label">
                                        <RadioButton
                                            id={'createNewSubCategory2'}
                                            checked={
                                                createNewSubCategoryFlag === 'createNewSubCategory'
                                            }
                                            name={'createNewSubCategory'}
                                            value={'createNewSubCategory'}
                                            onChange={(event: any) => {
                                                onClickOfRadioButton(event.target.value);
                                            }}
                                        />
                                        <Label
                                            style={{ paddingTop: '0rem' }}
                                            text="Sub category not found. Create new"></Label>
                                    </div>
                                </div>
                            }
                        </div>
                    ) : wizardScreen == 1 ? (
                        <div className="form-container">
                            <CategoryForm
                                fieldErrors={fieldErrors}
                                name={name}
                                handleNameChange={handleNameChange}
                                sortOrder={sortOrder}
                                handleSortOrderChange={handleSortOrderChange}
                                description={description}
                                handleDescription={handleDescription}
                            />
                        </div>
                    ) : (
                        <div className="form-container">
                            <SubcategoryForm
                                fieldErrors={fieldErrors}
                                name={subcatName}
                                handleNameChange={handleSubcatNameChange}
                                sortOrder={subcatSortOrder}
                                handleSortOrderChange={handleSubcatSortOrderChange}
                                description={subcatDescription}
                                handleDescription={handleSubcatDescription}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <div className="footer-container">
                        <div className="footer-container__left">
                            <Button
                                size="medium"
                                type="button"
                                disabled={primaryButtonDisable}
                                onClick={() => {
                                    if (createNewSubCategoryFlag != '' || targetModelData?.id) {
                                        if (createNewSubCategoryFlag == 'createNewCategory') {
                                            if (wizardScreen == 0) {
                                                setWizardScreen(1);
                                            } else if (wizardScreen == 1) {
                                                let errors = fieldErrors;
                                                if (!name.trim()) {
                                                    errors = addValidationError(
                                                        errors,
                                                        CategoryField.Name,
                                                        CATEGORY_NAME_EMPTY_ERROR
                                                    );
                                                }
                                                if (!sortOrder) {
                                                    errors = addValidationError(
                                                        errors,
                                                        CategoryField.SortOrder,
                                                        CATEGORY_ORDER_EMPTY_ERROR
                                                    );
                                                }
                                                if (errors.length === 0) {
                                                    setWizardScreen(2);
                                                }
                                                updateFieldErrors(errors);
                                            } else {
                                                let errors = fieldErrors;
                                                if (!subcatName.trim()) {
                                                    errors = addValidationError(
                                                        errors,
                                                        SubCategoryField.Name,
                                                        SUB_CATEGORY_NAME_EMPTY_ERROR
                                                    );
                                                }
                                                if (!subcatSortOrder) {
                                                    errors = addValidationError(
                                                        errors,
                                                        SubCategoryField.SortOrder,
                                                        SUB_CATEGORY_ORDER_EMPTY_ERROR
                                                    );
                                                }
                                                if (errors.length === 0) {
                                                    saveHandler(createNewSubCategoryFlag, {
                                                        targetModelData: targetModelData,
                                                        data: {
                                                            catName: name,
                                                            catOrder: sortOrder,
                                                            catDescription: description,
                                                            subCatName: subcatName,
                                                            subCatOrder: subcatSortOrder,
                                                            subCatDescription: subcatDescription
                                                        }
                                                    });

                                                    cancelMoveWizardPopup();
                                                }
                                                updateFieldErrors(errors);
                                            }
                                        } else if (
                                            createNewSubCategoryFlag == 'createNewSubCategory'
                                        ) {
                                            // Only sub category with existing category
                                            openPopUpToCreateNewSubCategory();
                                        } else {
                                            saveHandler(createNewSubCategoryFlag, {
                                                targetModelData: targetModelData,
                                                data: null
                                            });
                                        }
                                    } else {
                                        setPopupDetails({
                                            title: MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE,
                                            text: MOVE_VALIDATION_ERROR_MESSAGE,
                                            type: ''
                                        });
                                        setShowPopup(true);
                                    }
                                }}>
                                {(wizardScreen == 0 &&
                                    (createNewSubCategoryFlag == 'createNewSubCategory' ||
                                        createNewSubCategoryFlag == 'createNewCategory')) ||
                                wizardScreen == 1
                                    ? ACTION_ITEMS.NEXT_BUTTON
                                    : wizardScreen == 0
                                    ? ACTION_ITEMS.SUBMIT_BUTTON
                                    : ACTION_ITEMS.SUBMIT_BUTTON}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    cancelMoveWizardPopup();
                                    updateFieldErrors([]);
                                }}>
                                {ACTION_ITEMS.CANCEL_BUTTON}
                            </Button>
                        </div>
                        <div className="footer-container__right">
                            {wizardScreen == 1 || wizardScreen == 2 ? (
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    onClick={() => {
                                        onBackButtonClicked();
                                        updateFieldErrors([]);
                                    }}>
                                    {ACTION_ITEMS.BACK_BUTTON}
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={false}
                type={MessageType.Error}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => {
                    setShowPopup(false);
                }}
                onSecondaryAction={() => {
                    setShowPopup(false);
                }}
            />
        </>
    );
}
